import { useEffect, useRef, useState } from "react";
import { IoArrowDownOutline, IoRefreshOutline } from "react-icons/io5";
import { tapLight, tapSuccess } from "@/utils/haptics";

/**
 * Native-feeling pull-to-refresh container.
 * Wrap the scrollable content that lives inside a page.
 * Only triggers when the *page* is scrolled to top (scrollTop === 0).
 *
 * <PullToRefresh onRefresh={loadData}>
 *   ...content...
 * </PullToRefresh>
 */
export const PullToRefresh = ({ onRefresh, threshold = 70, disabled = false, children }) => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const activeRef = useRef(false);
  const passedHapticRef = useRef(false);

  useEffect(() => {
    if (disabled) return;

    const atTop = () => {
      // Use both window and document scroll containers because our .mobile-frame relies on window scroll.
      const y = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      return y <= 0;
    };

    const onStart = (e) => {
      if (refreshing) return;
      if (!atTop()) return;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      startYRef.current = y;
      activeRef.current = true;
      passedHapticRef.current = false;
    };

    const onMove = (e) => {
      if (!activeRef.current || startYRef.current == null || refreshing) return;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = y - startYRef.current;
      if (dy <= 0) { setPull(0); return; }
      // rubber-band feel
      const eased = Math.min(dy * 0.55, threshold * 1.6);
      setPull(eased);
      if (eased > threshold && !passedHapticRef.current) {
        passedHapticRef.current = true;
        tapLight();
      } else if (eased <= threshold && passedHapticRef.current) {
        passedHapticRef.current = false;
      }
      // Only preventDefault when we're actively pulling to avoid blocking normal scroll
      if (dy > 12 && e.cancelable) e.preventDefault();
    };

    const onEnd = async () => {
      if (!activeRef.current) return;
      const doTrigger = pull > threshold;
      activeRef.current = false;
      startYRef.current = null;
      if (doTrigger) {
        setRefreshing(true);
        setPull(threshold);
        try {
          await onRefresh?.();
          tapSuccess();
        } catch { /* handled by caller */ }
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);

    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [onRefresh, threshold, disabled, pull, refreshing]);

  const showIndicator = pull > 0 || refreshing;
  const rotation = Math.min(1, pull / threshold) * 180;
  const scale = Math.min(1, pull / (threshold * 0.7));

  return (
    <>
      {showIndicator && (
        <div
          className="tf-ptr-indicator"
          data-testid="pull-to-refresh-indicator"
          style={{
            transform: `translateY(${Math.min(pull, threshold + 20)}px) scale(${refreshing ? 1 : Math.max(0.4, scale)})`,
            opacity: refreshing ? 1 : Math.min(1, pull / threshold),
          }}
        >
          {refreshing ? (
            <IoRefreshOutline size={22} className="tf-ptr-spinning" />
          ) : (
            <IoArrowDownOutline
              size={22}
              style={{ transform: `rotate(${180 - rotation}deg)`, transition: "transform 60ms linear" }}
            />
          )}
        </div>
      )}
      <div
        style={{
          transform: refreshing ? `translateY(${threshold * 0.6}px)` : `translateY(${pull * 0.6}px)`,
          transition: activeRef.current ? "none" : "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </>
  );
};
